import type { ReactNode } from 'react';

import type { TEmailCapturePopupElement } from '@shared/dream-components';
import { EmailCapturePopupElement } from '@shared/dream-components';

import { usePostPageContext } from '~/context/usePostPageContext';
import { useEmailCaptureModal } from '~/hooks/useEmailCaptureModal';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { StaticPageLoaderData } from '~/services/static_page';

import { ModalProvider } from './context/ModalContext';

interface Props {
  node: TEmailCapturePopupElement;
  children: ReactNode | ReactNode[];
}

const EmailCapturePopup = ({ node, children }: Props) => {
  const { page } = useTypedLoaderData<StaticPageLoaderData>();
  const { isEmailCapturePopupEnabled } = usePostPageContext() || { isEmailCapturePopupEnabled: false };

  const parsedCookieExpiryDays =
    typeof node.attrs?.cookieExpiryDays === 'string'
      ? Number.parseInt(node.attrs?.cookieExpiryDays)
      : node.attrs?.cookieExpiryDays;

  const isPostPage = page.viewable_page_version.name === 'Post';
  const isDisabled = isPostPage && !isEmailCapturePopupEnabled;

  const { isOpen, onClose } = useEmailCaptureModal({
    showAfterPercentageScrolled: node.attrs?.showAfterPercentageScrolled,
    cookieExpiryDays: !Number.isNaN(parsedCookieExpiryDays) ? parsedCookieExpiryDays : undefined,
    pageId: page.id,
    enabled: !isDisabled,
    isOnPostPage: isPostPage,
  });

  return (
    <ModalProvider onClose={onClose}>
      <EmailCapturePopupElement
        element={node}
        isLive
        isOpen={isOpen && !isDisabled}
        onOpenChange={onClose}
        placement={isPostPage && 'center'}
      >
        {children}
      </EmailCapturePopupElement>
    </ModalProvider>
  );
};

export default EmailCapturePopup;
